import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowsClockwise, ListBullets } from "@phosphor-icons/react";
import { fmtWhen } from "./shared";

const LEVEL = { info: "bg-emerald-50 text-emerald-700 border-emerald-200", warn: "bg-amber-50 text-amber-700 border-amber-200", error: "bg-red-50 text-red-700 border-red-200" };
const NAMES = { xero: "Xero", quickbooks: "QuickBooks", gocardless: "GoCardless", google_calendar: "Google", microsoft_calendar: "Outlook", ical: "iCal", twilio: "SMS", zapier: "Zapier" };

export default function SyncLog() {
  const [items, setItems] = useState([]);
  const [provider, setProvider] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/integrations/logs", { params: { provider: provider === "all" ? undefined : provider, limit: 80 } });
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the sync log"); }
    finally { setLoading(false); }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="integrations-sync-log">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold flex items-center gap-2"><ListBullets size={16} /> Sync log</h3>
        <div className="flex items-center gap-2">
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="sync-log-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All integrations</SelectItem>
              {Object.entries(NAMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="sync-log-refresh-btn"><ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /></Button>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground px-4 py-6 text-center" data-testid="sync-log-empty">Nothing synced yet — connect an integration above and activity will show here.</p>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-auto">
          {items.map((l) => (
            <li key={l.id} className="px-4 py-2 flex items-start gap-3 text-xs" data-testid={`sync-log-row-${l.id}`}>
              <span className="text-muted-foreground whitespace-nowrap w-28 shrink-0">{fmtWhen(l.at)}</span>
              <Badge variant="outline" className={`shrink-0 text-[10px] ${LEVEL[l.level] || ""}`}>{NAMES[l.provider] || l.provider}</Badge>
              <span className="flex-1">
                <span className="font-medium">{(l.action || "").replace(/_/g, " ")}</span> — {l.message}
                {l.entity_type === "invoice" && l.entity_id && <a href={`/invoices/${l.entity_id}`} className="ml-1 text-primary underline">open</a>}
                {l.entity_type === "job" && l.entity_id && <a href={`/jobs/${l.entity_id}`} className="ml-1 text-primary underline">open</a>}
                {l.entity_type === "client" && l.entity_id && <a href={`/clients/${l.entity_id}`} className="ml-1 text-primary underline">open</a>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
